import { useCallback, useEffect, useRef, useState } from 'react'

// Keeps a live-feed scroll container pinned to the bottom — but only while the
// reader is already there. The moment they scroll up to read, new content
// appended below no longer yanks their position; instead `hasNew` flips true so
// the caller can show a "jump to latest" affordance. Tapping it (scrollToBottom)
// re-pins. Switching feeds (resetKey change) jumps to the bottom instantly and
// treats the reader as pinned again.
//
// Note this only governs the forced scroll. Content appended *below* the
// viewport never moves an unpinned reader's absolute position on its own — the
// browser preserves scrollTop and the feed just grows downward. The old bug was
// the unconditional scrollToBottom on every new entry; this gates it.
// contentSignal: a value that changes exactly when content is appended at the
// bottom (a new entry). Pass a length where the feed only ever grows downward
// within a mount, or the last item's id where the same list can also be
// prepended with history — so back-filling older items doesn't trip `hasNew`.
export function useStickToBottom(
  contentSignal: string | number,
  resetKey: string | number,
) {
  const scrollRef = useRef<HTMLDivElement>(null)
  // Whether the viewport is currently at (or within a hair of) the bottom.
  // A ref, not state: the content effect must read the live value without
  // re-subscribing, and flipping it should not itself trigger a render.
  const atBottomRef = useRef(true)
  const [hasNew, setHasNew] = useState(false)

  // Slack in px: being this close to the bottom still counts as "pinned", so a
  // reader who is essentially at the end isn't stranded by sub-pixel rounding
  // or a partially-visible last row.
  const THRESHOLD = 64

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
    atBottomRef.current = true
    setHasNew(false)
  }, [])

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const atBottom = distanceFromBottom <= THRESHOLD
    atBottomRef.current = atBottom
    // Reaching the bottom (by scroll or momentum) clears the new-content badge.
    if (atBottom) setHasNew((n) => (n ? false : n))
  }, [])

  // Switched feed (agent / room): start pinned at the bottom, no badge.
  useEffect(() => {
    atBottomRef.current = true
    setHasNew(false)
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [resetKey])

  // New content arrived: stay pinned if the reader was at the bottom, otherwise
  // leave their position alone and raise the badge.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight
    } else {
      setHasNew(true)
    }
    // resetKey intentionally excluded: the reset effect above owns feed switches
    // and runs first, so this fire (same render) already sees atBottom = true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSignal])

  return { scrollRef, onScroll, hasNew, scrollToBottom }
}
